import { useEffect, useRef } from 'react';
import { useWebSocket } from './useWebSocket';
import { useNotificationStore } from '../store/notifications';
import type { NotificationCategory, ToastType } from '../store/notifications';

interface AlertPayload {
  id?: string;
  title?: string;
  description?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  entity_ids?: string[];
}

interface GraphUpdatePayload {
  action?: string;
  entity_count?: number;
  message?: string;
}

interface PipelinePayload {
  pipeline_id?: string;
  pipeline_name?: string;
  status?: 'completed' | 'failed' | 'running';
  message?: string;
  duration_ms?: number;
}

interface UseNotificationsOptions {
  soundEnabled?: boolean;
}

const severityToToastType: Record<string, ToastType> = {
  critical: 'error',
  high: 'error',
  medium: 'warning',
  low: 'info',
  info: 'info',
};

function playAlertSound() {
  try {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.setValueAtTime(660, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  } catch {
    // Audio not available, ignore
  }
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { soundEnabled = false } = options;
  const addToast = useNotificationStore((s) => s.addToast);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;

  const { subscribe, isConnected } = useWebSocket();

  // Listen for alerts
  useEffect(() => {
    const unsub = subscribe('alert', (message) => {
      const payload = message.payload as AlertPayload;
      const severity = payload.severity || 'medium';
      const toastType = severityToToastType[severity] || 'warning';

      // Add notification to panel
      addNotification({
        type: toastType,
        category: 'alerts' as NotificationCategory,
        title: payload.title || 'Nouvelle alerte',
        message: payload.description || 'Une alerte a ete declenchee',
        link: payload.id ? `/alerts?id=${payload.id}` : '/alerts',
      });

      // Add toast
      addToast({
        type: severity === 'critical' ? 'alert' : toastType,
        title: payload.title || 'Nouvelle alerte',
        message: payload.description || 'Une alerte a ete declenchee',
        duration: severity === 'critical' ? 10000 : 5000,
        action: {
          label: "Voir l'alerte",
          onClick: () => {
            window.location.href = payload.id ? `/alerts?id=${payload.id}` : '/alerts';
          },
        },
      });

      // Sound for critical
      if ((severity === 'critical' || severity === 'high') && soundEnabledRef.current) {
        playAlertSound();
      }
    });

    return unsub;
  }, [subscribe, addToast, addNotification]);

  // Listen for graph updates
  useEffect(() => {
    const unsub = subscribe('graph_update', (message) => {
      const payload = message.payload as GraphUpdatePayload;

      addNotification({
        type: 'info',
        category: 'system' as NotificationCategory,
        title: 'Mise a jour du graphe',
        message: payload.message || `Le graphe a ete mis a jour (${payload.entity_count || 0} entites)`,
        link: '/graph',
      });

      addToast({
        type: 'info',
        title: 'Graphe mis a jour',
        message: payload.message || 'Nouvelles donnees disponibles',
        duration: 4000,
      });
    });

    return unsub;
  }, [subscribe, addToast, addNotification]);

  // Listen for pipeline status
  useEffect(() => {
    const unsub = subscribe('pipeline_status', (message) => {
      const payload = message.payload as PipelinePayload;
      const pipelineName = payload.pipeline_name || 'Pipeline';

      if (payload.status === 'completed') {
        addNotification({
          type: 'success',
          category: 'system' as NotificationCategory,
          title: `${pipelineName} termine`,
          message: payload.message || `Le pipeline s'est termine avec succes`,
        });
        addToast({
          type: 'success',
          title: `${pipelineName} termine`,
          message: payload.message || 'Execution terminee avec succes',
          duration: 5000,
        });
      } else if (payload.status === 'failed') {
        addNotification({
          type: 'error',
          category: 'system' as NotificationCategory,
          title: `${pipelineName} echoue`,
          message: payload.message || "Le pipeline a rencontre une erreur",
        });
        addToast({
          type: 'error',
          title: `${pipelineName} echoue`,
          message: payload.message || "Erreur lors de l'execution",
          duration: 8000,
        });
      }
    });

    return unsub;
  }, [subscribe, addToast, addNotification]);

  // Listen for anomaly detection
  useEffect(() => {
    const unsub = subscribe('anomaly_detected', (message) => {
      const payload = message.payload as { entity_name?: string; anomaly_type?: string; severity?: string; entity_id?: string };

      addNotification({
        type: payload.severity === 'high' || payload.severity === 'critical' ? 'error' : 'warning',
        category: 'alerts' as NotificationCategory,
        title: 'Anomalie detectee',
        message: `${payload.entity_name || 'Entite'} — ${payload.anomaly_type || 'anomalie'}`,
        link: payload.entity_id ? `/entities/${payload.entity_id}` : '/anomaly',
      });

      addToast({
        type: 'warning',
        title: 'Anomalie detectee',
        message: `${payload.entity_name || 'Entite'} — ${payload.anomaly_type || 'anomalie'}`,
        duration: 6000,
      });
    });
    return unsub;
  }, [subscribe, addToast, addNotification]);

  // Listen for workflow transitions
  useEffect(() => {
    const unsub = subscribe('workflow_transition', (message) => {
      const payload = message.payload as { title?: string; new_status?: string; workflow_id?: string };

      addNotification({
        type: 'info',
        category: 'investigations' as NotificationCategory,
        title: 'Workflow mis a jour',
        message: `${payload.title || 'Investigation'} → ${payload.new_status || 'nouveau statut'}`,
        link: '/workflows',
      });

      addToast({
        type: 'info',
        title: 'Workflow mis a jour',
        message: `${payload.title || 'Investigation'} → ${payload.new_status || ''}`,
        duration: 4000,
      });
    });
    return unsub;
  }, [subscribe, addToast, addNotification]);

  // Listen for entity updates
  useEffect(() => {
    const unsub = subscribe('entity_update', (message) => {
      const payload = message.payload as { entity_name?: string; action?: string; message?: string };

      addNotification({
        type: 'info',
        category: 'investigations' as NotificationCategory,
        title: 'Entite mise a jour',
        message: payload.message || `${payload.entity_name || 'Entite'} a ete ${payload.action || 'modifiee'}`,
        link: '/entities',
      });
    });

    return unsub;
  }, [subscribe, addNotification]);

  return { isConnected };
}
